/**
 * Category Orbit — zentraler Hub mit Kategorie-Chips im Ring (kinetisch).
 *
 * Die „Kategorie-Übersicht" als ruhiges, premium Schaubild: in der Mitte
 * eine Glas-Disc mit Center-Label (z. B. „7 Kategorien. Eine App.") plus
 * optionalem Eyebrow, drumherum ein gleichmäßig verteilter Ring aus
 * Kategorie-Chips (runde Kachel + Lucide-Icon + kleines Label). Faint
 * Orbit-Ellipse, Connector-Linien und ein weicher Glow-Ring geben Tiefe.
 *
 * Motion:
 *   - Ellipse + Glow blenden auf, der Hub poppt zuerst (back.out).
 *   - Ein gemeinsamer Ring-Container dreht sich von -90° auf 0° (back.out),
 *     während die Chips gestaffelt skalieren/aufblenden — am Ende steht
 *     alles aufrecht (keine Gegen-Rotation nötig).
 *   - Connector-Linien und Chip-Labels blenden nach dem Settle ein.
 *   - Danach dezentes Dauer-Leben: Hub-Float + Glow-Atmen (repeat:-1, yoyo).
 *
 * Items-Format:  "Label | iconKey"
 *   z. B. "Restaurants | shop"  (iconKey = gültiger ICON_PATHS-Key)
 *
 * Inputs:
 *   - text           → Center-Label (Default „7 Kategorien.\nEine App.";
 *                      literales \n wird in Zeilen aufgeteilt)
 *   - extras.eyebrow → Uppercase-Label über dem Center-Label (Default „ALLYVER")
 *   - accent         → Akzent-Token (primary | secondary | accent)
 *   - items          → 5–8 Chips „Label | iconKey", gleichmäßig im Ring verteilt
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, gsapSetLine,
  makeTl, typeSize, weights, withAlpha, colors as themeColors,
} from "../../reel/sceneShared.js";
import { lucideIcon, splitItem } from "../visual/_mockHelpers.js";

export const categoryOrbit: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const violet = themeColors.secondary;
  const pink = themeColors.accent;
  const palette = [accent, violet, pink, accent, violet, pink, accent, violet];
  const isVertical = ctx.format === "9:16";

  const eyebrow = inputs.extras?.eyebrow != null ? String(inputs.extras.eyebrow) : "ALLYVER";
  const centerRaw = inputs.text ?? "7 Kategorien.\nEine App.";
  const centerLines = centerRaw.split(/\\n|\n/).map((l) => l.trim()).filter(Boolean);

  const items = (inputs.items?.length
    ? inputs.items
    : [
        "Restaurants | shop",
        "Shops | store",
        "Taxis | taxi",
        "Hotels | hotel",
        "Events | event",
        "Services | service",
        "Lokal | heart",
      ]
  ).slice(0, 8);

  // Mindestens 5 Chips, damit der Ring ausbalanciert wirkt.
  const parsed = items.map((raw, i) => {
    const [label, iconRaw] = splitItem(raw);
    return {
      label: (label ?? "").trim(),
      icon: (iconRaw ?? "").trim() || "store",
      color: palette[i % palette.length] ?? accent,
    };
  });
  const N = Math.max(parsed.length, 1);

  const md = typeSize("md", ctx.format);
  const sm = typeSize("sm", ctx.format);

  // Ring-Geometrie aus Format ableiten (px, im Stage-Koordinatensystem).
  const ringRadius = isVertical ? 360 : 300;
  const stageSize = ringRadius * 2;
  const hubSize = isVertical ? 320 : 288;
  const chipSize = isVertical ? 150 : 130;
  const chipIconBox = Math.round(chipSize * 0.42);

  const eyebrowPx = isVertical ? sm * 0.52 : sm * 0.5;
  const centerPx = isVertical ? md * 0.62 : md * 0.52;
  const chipLabelPx = isVertical ? sm * 0.86 : sm * 0.72;

  const wrapId = `${clipPrefix}-wrap`;
  const stageId = `${clipPrefix}-stage`;
  const glowId = `${clipPrefix}-glow`;
  const ellipseId = `${clipPrefix}-ellipse`;
  const ringId = `${clipPrefix}-ring`;
  const hubId = `${clipPrefix}-hub`;
  const chipIds = parsed.map((_, i) => `${clipPrefix}-chip-${i}`);
  const lblIds = parsed.map((_, i) => `${clipPrefix}-lbl-${i}`);
  const lineIds = parsed.map((_, i) => `${clipPrefix}-line-${i}`);

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "spotlight", accent });

  // Finale Ring-Positionen (Start oben bei -90°, gleichmäßig im Kreis).
  const chipGeom = parsed.map((_, i) => {
    const angle = -Math.PI / 2 + (i / N) * Math.PI * 2;
    return {
      x: Math.round(Math.cos(angle) * ringRadius),
      y: Math.round(Math.sin(angle) * ringRadius),
      deg: angle * (180 / Math.PI),
    };
  });

  function chip(i: number, it: (typeof parsed)[number]): string {
    const g = chipGeom[i];
    const color = it.color;
    // Äußerer Pos-Wrapper macht NUR die CSS-Translation (GSAP fasst ihn nie
    // an); der innere Chip wird von GSAP skaliert/eingeblendet, ohne dass
    // die Translation verloren geht.
    return `<div style="position:absolute;left:50%;top:50%;transform:translate(calc(-50% + ${g.x}px), calc(-50% + ${g.y}px));">
      <div id="${chipIds[i]}" style="display:flex;flex-direction:column;align-items:center;gap:${isVertical ? 12 : 10}px;width:${chipSize}px;will-change:transform,opacity;">
        <div style="width:${chipSize}px;height:${chipSize}px;border-radius:9999px;display:flex;align-items:center;justify-content:center;background:radial-gradient(circle at 32% 28%, ${withAlpha(color, 0.72)} 0%, ${withAlpha(themeColors.bg, 0.7)} 78%);border:1px solid ${withAlpha(color, 0.6)};box-shadow:0 18px 44px rgba(0,0,0,0.4), 0 0 30px ${withAlpha(color, 0.45)}, inset 0 1px 0 ${withAlpha(themeColors.fg, 0.14)};backdrop-filter:blur(6px);color:${themeColors.fg};">
          ${lucideIcon(it.icon, chipIconBox, themeColors.fg, 2.2)}
        </div>
        ${it.label ? `<div id="${lblIds[i]}" style="font-family:'Outfit',sans-serif;font-weight:${weights.semibold};font-size:${chipLabelPx}px;letter-spacing:0.2px;color:${withAlpha(themeColors.fg, 0.86)};text-align:center;white-space:nowrap;text-shadow:0 2px 12px rgba(0,0,0,0.5);">${escapeHtml(it.label)}</div>` : ""}
      </div>
    </div>`;
  }

  // Connector-Linien laufen vom Hub-Zentrum nach außen zu jedem Chip.
  function connector(i: number): string {
    const g = chipGeom[i];
    const len = ringRadius - chipSize * 0.42;
    return `<div id="${lineIds[i]}" style="position:absolute;left:50%;top:50%;width:${Math.max(0, Math.round(len))}px;height:2px;transform-origin:0 50%;transform:rotate(${g.deg.toFixed(2)}deg);background:linear-gradient(90deg, ${withAlpha(it_color(i), 0.0)} 0%, ${withAlpha(it_color(i), 0.45)} 100%);border-radius:2px;"></div>`;
  }
  function it_color(i: number): string {
    return parsed[i]?.color ?? accent;
  }

  const chipsHtml = parsed.map((it, i) => chip(i, it)).join("");
  const linesHtml = parsed.map((_, i) => connector(i)).join("");
  const centerHtml = centerLines
    .map(
      (line) =>
        `<div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${centerPx}px;line-height:1.04;letter-spacing:-1px;color:${themeColors.fg};text-wrap:balance;">${escapeHtml(line)}</div>`,
    )
    .join("");

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;padding:${isVertical ? "100px 60px" : "64px 90px"};overflow:hidden;">
    <div id="${stageId}" style="position:relative;width:${stageSize}px;height:${stageSize}px;flex-shrink:0;">
      <div style="position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);">
        <div id="${glowId}" style="width:${Math.round(stageSize * 0.92)}px;height:${Math.round(stageSize * 0.92)}px;border-radius:9999px;background:radial-gradient(circle, ${withAlpha(accent, 0.28)} 0%, ${withAlpha(accent, 0.08)} 45%, ${withAlpha(accent, 0.0)} 70%);filter:blur(6px);will-change:transform,opacity;"></div>
      </div>
      <div style="position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);">
        <div id="${ellipseId}" style="width:${stageSize}px;height:${stageSize}px;border-radius:9999px;border:1.5px solid ${withAlpha(themeColors.fg, 0.14)};box-shadow:inset 0 0 60px ${withAlpha(accent, 0.12)};will-change:transform,opacity;"></div>
      </div>
      <div id="${ringId}" style="position:absolute;inset:0;will-change:transform;">
        ${linesHtml}
        ${chipsHtml}
      </div>
      <div style="position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);">
        <div id="${hubId}" style="width:${hubSize}px;height:${hubSize}px;border-radius:9999px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${isVertical ? 14 : 12}px;padding:${isVertical ? "0 36px" : "0 30px"};text-align:center;background:radial-gradient(circle at 38% 30%, ${withAlpha(themeColors.fg, 0.14)} 0%, ${withAlpha(themeColors.bg, 0.82)} 70%);border:1px solid ${withAlpha(themeColors.fg, 0.18)};box-shadow:0 30px 80px rgba(0,0,0,0.5), 0 0 70px ${withAlpha(accent, 0.4)}, inset 0 1px 0 ${withAlpha(themeColors.fg, 0.16)};backdrop-filter:blur(10px);will-change:transform,opacity;box-sizing:border-box;">
          ${eyebrow ? `<div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${eyebrowPx}px;letter-spacing:5px;text-transform:uppercase;color:${accent};">${escapeHtml(eyebrow)}</div>` : ""}
          <div style="display:flex;flex-direction:column;gap:2px;align-items:center;">${centerHtml}</div>
        </div>
      </div>
    </div>
  </div>`;

  const init = [
    gsapSetLine(`#${glowId}`, { opacity: 0, scale: 0.8, transformOrigin: "50% 50%" }),
    gsapSetLine(`#${ellipseId}`, { opacity: 0, scale: 0.85, transformOrigin: "50% 50%" }),
    gsapSetLine(`#${ringId}`, { rotation: -90, transformOrigin: "50% 50%" }),
    gsapSetLine(`#${hubId}`, { opacity: 0, scale: 0.6, transformOrigin: "50% 50%" }),
    ...chipIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, scale: 0, transformOrigin: "50% 50%" })),
    ...lblIds.map((id, i) => (parsed[i].label ? gsapSetLine(`#${id}`, { opacity: 0, y: 8 }) : "")),
    ...lineIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0 })),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [];

  // 1) Tiefe zuerst: Ellipse + Glow.
  tweens.push(t.to(`#${ellipseId}`, { opacity: 1, scale: 1, duration: 0.7, ease: "power3.out" }, 0.1));
  tweens.push(t.to(`#${glowId}`, { opacity: 1, scale: 1, duration: 0.8, ease: "power2.out" }, 0.15));

  // 2) Hub poppt rein.
  tweens.push(t.to(`#${hubId}`, { opacity: 1, scale: 1, duration: 0.6, ease: "back.out(1.8)" }, 0.2));

  // 3) Ring dreht sich von -90° auf 0° ins Finale.
  const spinAt = 0.45;
  const spinDur = 1.15;
  tweens.push(t.to(`#${ringId}`, { rotation: 0, duration: spinDur, ease: "back.out(1.4)" }, spinAt));

  // 4) Chips skalieren/erscheinen gestaffelt während des Spins.
  const chipWindow = Math.min(spinDur, Math.max(0.7, clipDurationSec * 0.4));
  const chipStep = chipWindow / N;
  parsed.forEach((_, i) => {
    const at = spinAt + 0.15 + i * chipStep;
    tweens.push(t.to(`#${chipIds[i]}`, { opacity: 1, scale: 1, duration: 0.5, ease: "back.out(1.7)" }, at));
  });

  // 5) Connector-Linien + Labels nach dem Settle.
  const settleAt = spinAt + spinDur;
  parsed.forEach((it, i) => {
    tweens.push(t.to(`#${lineIds[i]}`, { opacity: 1, duration: 0.45, ease: "power2.out" }, settleAt - 0.3 + i * 0.05));
    if (it.label) tweens.push(t.to(`#${lblIds[i]}`, { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" }, settleAt + i * 0.06));
  });

  // 6) Dezentes Dauer-Leben: Hub atmet/floatet, Glow pulsiert.
  const lifeAt = Math.min(settleAt + 0.4, Math.max(0.2, clipDurationSec - 0.5));
  tweens.push(t.to(`#${hubId}`, { y: isVertical ? -10 : -8, duration: 2.6, ease: "sine.inOut", repeat: -1, yoyo: true }, lifeAt));
  tweens.push(t.to(`#${glowId}`, { scale: 1.06, opacity: 0.82, duration: 2.2, ease: "sine.inOut", repeat: -1, yoyo: true }, lifeAt));

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 10));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
